import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Paper,
  Slider,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  PaletteMode,
  Typography,
  useTheme,
} from "@mui/material";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceArea,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useTranslation } from "react-i18next";
import OrbLoader from "../../common/OrbLoader";
import {
  useGetWipDailySeriesQuery,
  WIPCampaignMetrics,
  WipDailySeriesPoint,
} from "../../../store/apis/wipApi";
import {
  fmtInt,
  fmtMoney,
  fmtMoneyDetailed,
  fmtPct,
} from "../../../utils/format";
import { formatFlexible, NUMBER_LOCALE } from "../../../utils/numberFormat";
import {
  metricSeriesColor,
  seriesColor,
} from "../../client/clientFormatViewClient";
import WipGoalColumns, { GoalColumn } from "./WipGoalColumns";

/** Units per cost unit: CPM/CPMV price a thousand, the rest price one. */
const KPI_DIVISOR: Record<string, number> = { CPM: 1000, CPMV: 1000 };

/**
 * Mismo código de colores que la vista de cliente: impresiones azul, clics
 * naranja y dinero (inversión y costes por unidad) verde. Las métricas que no
 * tienen color propio allí llevan uno fijo de su paleta general (conversiones
 * el mismo que en cliente). Lo real va continuo; su ideal, del mismo color en
 * discontinua.
 */
const VOLUME_PALETTE_SLOT: Record<string, number> = {
  conversions: 4,
  completeViews: 6,
  engagements: 3,
};
const volumeColor = (metric: string, mode: PaletteMode): string =>
  metric === "impressions" || metric === "clicks"
    ? metricSeriesColor(metric, 0, mode)
    : seriesColor(VOLUME_PALETTE_SLOT[metric] ?? 0, mode);

const compact = new Intl.NumberFormat(NUMBER_LOCALE, {
  notation: "compact",
  maximumFractionDigits: 1,
});

/** "2026-09-14" → "14/09" without going through the browser time zone. */
const shortDay = (day: string) => `${day.slice(8, 10)}/${day.slice(5, 7)}`;

/** "2026-09-14" → "2026-09-15", en UTC para no depender de la zona del navegador. */
const nextDay = (day: string) => {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  return d.toISOString().slice(0, 10);
};

type ChartMode = "cumulative" | "daily";
type ChartHeight = "normal" | "tall" | "xtall";

const HEIGHT_PX: Record<ChartHeight, number> = {
  normal: 320,
  tall: 480,
  xtall: 640,
};

// Acumulado/diario y altura, compartidos por todas las subcampañas. Comodidad
// de quien mira: si el navegador no lo guarda, se vuelve a acumulado y normal.
const DISPLAY_KEY = "beapolo_wip_charts_display";

interface ChartDisplay {
  mode: ChartMode;
  height: ChartHeight;
}

const readDisplay = (): ChartDisplay => {
  try {
    const saved = JSON.parse(
      localStorage.getItem(DISPLAY_KEY) ?? "{}"
    ) as Partial<ChartDisplay>;
    return {
      mode: saved.mode === "daily" ? "daily" : "cumulative",
      height:
        saved.height && saved.height in HEIGHT_PX ? saved.height : "normal",
    };
  } catch {
    return { mode: "cumulative", height: "normal" };
  }
};

/** Lo de cada día a partir de dos acumulados seguidos (el primer día es su propio acumulado). */
const dayDelta = (
  current: number | null,
  previous: number | null | undefined
): number | null =>
  current == null ? null : previous == null ? current : current - previous;

interface TooltipEntry {
  dataKey?: string | number;
  name?: string;
  value?: number | null;
  color?: string;
  strokeDasharray?: string | number;
}

const ChartTooltip: React.FC<{
  active?: boolean;
  payload?: TooltipEntry[];
  label?: string;
  format: (n: number) => string;
}> = ({ active, payload, label, format }) => {
  const theme = useTheme();
  if (!active || !payload?.length || !label) return null;
  const entries = payload.filter((p) => p.value != null);
  if (entries.length === 0) return null;
  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 1,
        boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
        px: 1.5,
        py: 1,
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {shortDay(label)}/{label.slice(0, 4)}
      </Typography>
      {entries.map((p) => (
        <Stack
          key={String(p.dataKey)}
          direction="row"
          alignItems="center"
          spacing={1}
        >
          <Box
            sx={{
              width: 14,
              height: 0,
              borderTop: `2px ${p.strokeDasharray ? "dashed" : "solid"} ${p.color}`,
            }}
          />
          <Typography variant="body2" color="text.primary">
            {p.name}: <strong>{format(p.value as number)}</strong>
          </Typography>
        </Stack>
      ))}
    </Box>
  );
};

const ChartCard: React.FC<{
  title: string;
  subtitle?: string;
  height: number;
  children: React.ReactNode;
}> = ({ title, subtitle, height, children }) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${theme.palette.grey[200]}`,
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      <Box>
        <Typography variant="subtitle1" fontWeight={700} color="text.primary">
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
      <Box sx={{ width: "100%", height }}>{children}</Box>
    </Paper>
  );
};

const Kpi: React.FC<{ label: string; value: string; detail?: string }> = ({
  label,
  value,
  detail,
}) => (
  <Box sx={{ minWidth: 150 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h6" fontWeight={700} color="text.primary">
      {value}
    </Typography>
    {detail && (
      <Typography variant="caption" color="text.secondary">
        {detail}
      </Typography>
    )}
  </Box>
);

interface WipLineChartsProps {
  row: WIPCampaignMetrics;
}

/** Gráficas de una subcampaña: volumen, coste de plataforma y KPI, en acumulado o por día. */
const WipLineCharts: React.FC<WipLineChartsProps> = ({ row }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { data, isFetching, error } = useGetWipDailySeriesQuery(
    row.associatedCampaignId
  );
  const series = data?.data ?? null;
  const colors = {
    volume: volumeColor(
      series?.volumeMetric ?? "impressions",
      theme.palette.mode
    ),
    money: metricSeriesColor("investment", 0, theme.palette.mode),
  };
  const points: WipDailySeriesPoint[] = useMemo(
    () => series?.points ?? [],
    [series]
  );

  const volumeLabel = t(
    `wip.charts.metric.${series?.volumeMetric ?? "impressions"}`
  );
  const expected = row.expectedVolumeUntilYesterday;
  const deviation =
    expected > 0 ? ((row.accumulatedVolume - expected) / expected) * 100 : null;
  const realMargin =
    row.accumulatedClientCost > 0
      ? (1 - row.totalPlatformCost / row.accumulatedClientCost) * 100
      : null;

  const [display, setDisplay] = useState<ChartDisplay>(readDisplay);
  const { mode, height } = display;
  const daily = mode === "daily";
  useEffect(() => {
    try {
      localStorage.setItem(DISPLAY_KEY, JSON.stringify(display));
    } catch {
      // ignore quota errors
    }
  }, [display]);

  // Zoom: rango de días visible, por índice de punto. null = campaña entera;
  // se vuelve a la campaña entera al cambiar de subcampaña.
  const [zoom, setZoom] = useState<[number, number] | null>(null);
  useEffect(() => setZoom(null), [row.associatedCampaignId]);
  const lastIndex = Math.max(points.length - 1, 0);
  const [zoomStart, zoomEnd] = zoom
    ? [Math.min(zoom[0], lastIndex), Math.min(zoom[1], lastIndex)]
    : [0, lastIndex];

  // KPI del brief para el cliente (bruto), el de la tarjeta junto al real cliente.
  const briefKpi = row.grossUnitCost > 0 ? row.grossUnitCost : null;
  // La gráfica es interna: KPI real de plataforma (coste plataforma por unidad,
  // sin margen) frente al neto del brief, que es lo que debería costar en
  // plataforma. Sin margen, neto y bruto son lo mismo.
  const platformBriefKpi = row.marginApplies
    ? row.netUnitCost > 0
      ? row.netUnitCost
      : null
    : briefKpi;
  // Inversión inicial de plataforma (la del brief al crearlo): punto del final.
  const initialPlatformInversion = series?.platformInversion ?? 0;
  const chartPoints = useMemo(() => {
    const divisor = KPI_DIVISOR[row.objective] ?? 1;
    // Ideal de inversión de plataforma: la inversión actual del brief repartida
    // por igual entre los días del vuelo (mismo reparto lineal que el backend:
    // contando el primer y el último día).
    const dayMs = 86_400_000;
    const startMs = series?.startDate ? Date.parse(series.startDate) : NaN;
    const endMs = series?.endDate ? Date.parse(series.endDate) : NaN;
    const totalDays =
      endMs >= startMs ? Math.round((endMs - startMs) / dayMs) + 1 : 0;
    const idealCurrentCost = (date: string): number | null => {
      if (!totalDays) return null;
      const elapsed = Math.min(
        totalDays,
        Math.max(0, Math.round((Date.parse(date) - startMs) / dayMs) + 1)
      );
      return (row.currentPlatformInversion * elapsed) / totalDays;
    };
    const unitCost = (cost: number | null, volume: number | null) =>
      volume && cost != null ? (cost * divisor) / volume : null;
    // Predicción: a partir de ayer, cada día suma lo mismo que sumó ayer. Arranca
    // en ayer para que enlace con la línea real; sin ayer en la serie, no hay.
    const asOfIndex = points.findIndex((p) => p.date === series?.asOfDate);
    const asOf = asOfIndex >= 0 ? points[asOfIndex] : undefined;
    const beforeAsOf = asOfIndex > 0 ? points[asOfIndex - 1] : undefined;
    const lastVolume = asOf?.volume ?? null;
    const lastCost = asOf
      ? dayDelta(
          asOf.cumulativePlatformCost,
          beforeAsOf?.cumulativePlatformCost
        )
      : null;
    const forecast = (
      i: number,
      perDay: number | null,
      cumulativeAtAsOf: number | null | undefined
    ): number | null => {
      if (asOfIndex < 0 || i < asOfIndex || perDay == null) return null;
      if (daily) return perDay;
      return cumulativeAtAsOf == null
        ? null
        : cumulativeAtAsOf + (i - asOfIndex) * perDay;
    };
    return points.map((p, i) => {
      const prev = i > 0 ? points[i - 1] : undefined;
      const volume = daily ? p.volume : p.cumulativeVolume;
      const platformCost = daily
        ? dayDelta(p.cumulativePlatformCost, prev?.cumulativePlatformCost)
        : p.cumulativePlatformCost;
      return {
        date: p.date,
        volume,
        idealVolume: daily
          ? dayDelta(p.idealCumulativeVolume, prev?.idealCumulativeVolume)
          : p.idealCumulativeVolume,
        platformCost,
        idealPlatformCost: daily
          ? dayDelta(
              idealCurrentCost(p.date),
              prev ? idealCurrentCost(prev.date) : null
            )
          : idealCurrentCost(p.date),
        forecastVolume: forecast(i, lastVolume, asOf?.cumulativeVolume),
        forecastPlatformCost: forecast(
          i,
          lastCost,
          asOf?.cumulativePlatformCost
        ),
        realKpi: unitCost(platformCost, volume),
        briefKpi: platformBriefKpi,
      };
    });
  }, [
    points,
    row,
    platformBriefKpi,
    daily,
    series?.asOfDate,
    series?.startDate,
    series?.endDate,
  ]);
  const visiblePoints = useMemo(
    () => chartPoints.slice(zoomStart, zoomEnd + 1),
    [chartPoints, zoomStart, zoomEnd]
  );
  const chartHeight = HEIGHT_PX[height];

  // Columnas de la derecha de la gráfica de objetivo, siempre en acumulado:
  // ayer (lo conseguido) y el final del vuelo (la predicción, o lo conseguido
  // si ya terminó), cada una frente a su ideal.
  const goalColumns = useMemo((): GoalColumn[] => {
    if (!series) return [];
    const asOfDate = series.asOfDate;
    // Último día con datos que cuenta: ayer, o el último del eje si acaba antes.
    let asOfIndex = -1;
    points.forEach((p, i) => {
      if (p.date <= asOfDate && p.cumulativeVolume != null) asOfIndex = i;
    });
    if (asOfIndex < 0) return [];
    const asOf = points[asOfIndex];
    const columns: GoalColumn[] = [];
    if (asOf.idealCumulativeVolume != null) {
      columns.push({
        label: t("wip.charts.goalColumnYesterday"),
        value: asOf.cumulativeVolume ?? 0,
        ideal: asOf.idealCumulativeVolume,
        forecast: false,
      });
    }
    const endIndex = series.endDate
      ? points.findIndex((p) => p.date === series.endDate)
      : -1;
    const end = endIndex >= 0 ? points[endIndex] : undefined;
    if (end?.idealCumulativeVolume != null) {
      const finished = endIndex <= asOfIndex;
      // Misma predicción que la línea gris: cada día suma lo mismo que ayer.
      const value = finished
        ? (end.cumulativeVolume ?? 0)
        : (asOf.cumulativeVolume ?? 0) +
          (endIndex - asOfIndex) * (asOf.volume ?? 0);
      columns.push({
        label: t("wip.charts.goalColumnEnd", {
          date: shortDay(end.date),
        }),
        value,
        ideal: end.idealCumulativeVolume,
        forecast: !finished,
      });
    }
    return columns;
  }, [points, series, t]);

  const axisProps = {
    tick: { fontSize: 11, fill: theme.palette.text.secondary },
    tickLine: false,
    axisLine: { stroke: theme.palette.divider },
  };
  const xAxis = (
    <XAxis
      dataKey="date"
      tickFormatter={shortDay}
      minTickGap={24}
      {...axisProps}
    />
  );
  const grid = (
    <CartesianGrid stroke={theme.palette.divider} vertical={false} />
  );
  const asOfLine = series &&
    visiblePoints.some((p) => p.date === series.asOfDate) && (
      <ReferenceLine
        x={series.asOfDate}
        stroke={theme.palette.text.secondary}
        strokeDasharray="2 4"
        label={{
          value: t("wip.charts.yesterday"),
          // A la izquierda de la línea: a la derecha empieza la franja de hoy,
          // con su propia etiqueta.
          position: "insideTopRight",
          fontSize: 11,
          fill: theme.palette.text.secondary,
        }}
      />
    );
  // Hoy: la franja entre ayer y hoy, el día que aún se está acumulando.
  const today = series ? nextDay(series.asOfDate) : null;
  const todayBand = series &&
    today &&
    visiblePoints.some((p) => p.date === series.asOfDate) &&
    visiblePoints.some((p) => p.date === today) && (
      <ReferenceArea
        x1={series.asOfDate}
        x2={today}
        fill={theme.palette.text.primary}
        fillOpacity={0.06}
        stroke="none"
        label={{
          value: t("wip.charts.today"),
          position: "insideTop",
          fontSize: 11,
          fill: theme.palette.text.secondary,
        }}
      />
    );
  // Inversión plataforma inicial en el último día del vuelo, para ver cuánto se
  // ha movido respecto a la actual (la de la línea ideal). Solo en acumulado:
  // en diario no es comparable.
  const initialInvestmentDot = !daily &&
    series?.endDate &&
    initialPlatformInversion > 0 &&
    visiblePoints.some((p) => p.date === series.endDate) && (
      <ReferenceDot
        x={series.endDate}
        y={initialPlatformInversion}
        // Una rayita gris en vez de un punto: es una referencia, no un dato.
        shape={({ cx, cy }: { cx?: number; cy?: number }) =>
          cx == null || cy == null ? (
            <g />
          ) : (
            <line
              x1={cx - 9}
              x2={cx + 9}
              y1={cy}
              y2={cy}
              stroke={theme.palette.grey[500]}
              strokeWidth={3}
              strokeLinecap="round"
            />
          )
        }
        ifOverflow="extendDomain"
        label={{
          value: t("wip.charts.platformInversionInitial", {
            total: fmtMoney(initialPlatformInversion),
          }),
          position: "left",
          offset: 14,
          fontSize: 11,
          fill: theme.palette.text.primary,
        }}
      />
    );
  const forecastLine = (dataKey: string) => (
    <Line
      type="linear"
      dataKey={dataKey}
      name={t("wip.charts.forecast")}
      stroke={theme.palette.grey[500]}
      strokeWidth={2}
      strokeDasharray="4 4"
      dot={false}
      activeDot={false}
      isAnimationActive={false}
    />
  );
  const legend = (
    <Legend
      verticalAlign="top"
      align="left"
      iconType="plainline"
      // Separa la leyenda del gráfico: sin esto pisa la etiqueta más alta del eje Y.
      wrapperStyle={{
        fontSize: 12,
        color: theme.palette.text.primary,
        paddingBottom: 16,
      }}
    />
  );
  const chartMargin = { top: 12, right: 16, left: 0 };

  return (
    <Stack spacing={2}>
      <Box>
        <Typography
          variant="h6"
          fontWeight={700}
          color="text.primary"
          sx={{ overflowWrap: "anywhere" }}
        >
          {row.associatedCampaignName}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {[row.companyName, row.briefingName, row.platform]
            .filter(Boolean)
            .join(" · ")}
        </Typography>
      </Box>

      <Stack direction="row" flexWrap="wrap" gap={3}>
        <Kpi
          label={t("wip.charts.kpiVolume", { metric: volumeLabel })}
          value={fmtInt(row.accumulatedVolume)}
          detail={t("wip.charts.kpiVolumeDetail", {
            expected: fmtInt(Math.round(expected)),
            target: fmtInt(row.targetVolume),
          })}
        />
        <Kpi
          label={t("wip.charts.kpiDeviation")}
          value={
            deviation == null
              ? "—"
              : `${deviation > 0 ? "+" : ""}${fmtPct(deviation)}`
          }
          detail={t("wip.charts.kpiPacing", {
            pacing: fmtPct(row.pacingPercent, 0),
          })}
        />
        <Kpi
          label={t("wip.charts.kpiClientCost")}
          value={fmtMoney(row.accumulatedClientCost)}
          detail={t("wip.charts.kpiOf", {
            total: fmtMoney(row.totalInversion),
          })}
        />
        <Kpi
          label={t("wip.charts.kpiPlatformCost")}
          value={fmtMoney(row.totalPlatformCost)}
          detail={t("wip.charts.kpiOfCurrent", {
            total: fmtMoney(row.currentPlatformInversion),
          })}
        />
        <Kpi
          label={t("wip.charts.kpiReal", { objective: row.objective })}
          value={
            row.realUnitCostClient > 0
              ? fmtMoneyDetailed(row.realUnitCostClient)
              : "—"
          }
          detail={
            briefKpi != null
              ? t("wip.charts.kpiBriefDetail", {
                  value: fmtMoneyDetailed(briefKpi),
                })
              : undefined
          }
        />
        {row.marginApplies && (
          <Kpi
            label={t("wip.charts.kpiRealMargin")}
            value={realMargin == null ? "—" : fmtPct(realMargin)}
            detail={t("wip.charts.kpiPlannedMargin", {
              margin: fmtPct(row.margin),
            })}
          />
        )}
      </Stack>

      {error ? (
        <Alert severity="error">{t("wip.charts.loadError")}</Alert>
      ) : isFetching && !series ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <OrbLoader size="medium" label={t("common.loading")} />
        </Box>
      ) : series?.isDirectBuy ? (
        <Alert severity="info">{t("wip.charts.directBuy")}</Alert>
      ) : points.length === 0 ? (
        <Alert severity="info">{t("wip.charts.noData")}</Alert>
      ) : (
        <>
          <Stack direction="row" flexWrap="wrap" alignItems="center" gap={2}>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={mode}
              onChange={(_e, v: ChartMode | null) =>
                v && setDisplay((prev) => ({ ...prev, mode: v }))
              }
            >
              <ToggleButton value="cumulative" sx={{ textTransform: "none" }}>
                {t("wip.charts.modeCumulative")}
              </ToggleButton>
              <ToggleButton value="daily" sx={{ textTransform: "none" }}>
                {t("wip.charts.modeDaily")}
              </ToggleButton>
            </ToggleButtonGroup>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2" color="text.secondary">
                {t("wip.charts.height")}
              </Typography>
              <ToggleButtonGroup
                exclusive
                size="small"
                value={height}
                onChange={(_e, v: ChartHeight | null) =>
                  v && setDisplay((prev) => ({ ...prev, height: v }))
                }
              >
                <ToggleButton value="normal" sx={{ textTransform: "none" }}>
                  {t("wip.charts.heightNormal")}
                </ToggleButton>
                <ToggleButton value="tall" sx={{ textTransform: "none" }}>
                  {t("wip.charts.heightTall")}
                </ToggleButton>
                <ToggleButton value="xtall" sx={{ textTransform: "none" }}>
                  {t("wip.charts.heightXtall")}
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            {points.length > 2 && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{ flex: "1 1 280px", minWidth: 240, px: 1 }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {t("wip.charts.zoom")}
                </Typography>
                <Slider
                  size="small"
                  min={0}
                  max={lastIndex}
                  value={[zoomStart, zoomEnd]}
                  onChange={(_e, v) => {
                    const [a, b] = v as number[];
                    // Al menos dos días a la vista para que haya línea.
                    if (b > a) setZoom([a, b]);
                  }}
                  disableSwap
                  valueLabelDisplay="auto"
                  valueLabelFormat={(i: number) =>
                    points[i] ? shortDay(points[i].date) : ""
                  }
                  getAriaValueText={(i: number) => points[i]?.date ?? ""}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {shortDay(points[zoomStart].date)} –{" "}
                  {shortDay(points[zoomEnd].date)}
                </Typography>
                <Button
                  size="small"
                  disabled={!zoom}
                  onClick={() => setZoom(null)}
                  sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                >
                  {t("wip.charts.zoomReset")}
                </Button>
              </Stack>
            )}
          </Stack>

          <ChartCard
            title={t(
              daily ? "wip.charts.volumeTitleDaily" : "wip.charts.volumeTitle",
              { metric: volumeLabel }
            )}
            subtitle={t(
              daily
                ? "wip.charts.volumeSubtitleDaily"
                : "wip.charts.volumeSubtitle"
            )}
            height={chartHeight}
          >
            <Stack direction="row" spacing={2} sx={{ height: "100%" }}>
              <Box sx={{ flex: 1, minWidth: 0, height: "100%" }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={visiblePoints} margin={chartMargin}>
                    {grid}
                    {xAxis}
                    <YAxis
                      tickFormatter={(v: number) => compact.format(v)}
                      width={56}
                      {...axisProps}
                    />
                    <Tooltip
                      content={
                        <ChartTooltip format={(n) => fmtInt(Math.round(n))} />
                      }
                      cursor={{
                        stroke: theme.palette.text.secondary,
                        strokeWidth: 1,
                      }}
                    />
                    {legend}
                    {todayBand}
                    {asOfLine}
                    <Line
                      type="monotone"
                      dataKey="volume"
                      name={t("wip.charts.real")}
                      stroke={colors.volume}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{
                        r: 4,
                        strokeWidth: 2,
                        stroke: theme.palette.background.paper,
                      }}
                      isAnimationActive={false}
                    />
                    <Line
                      type="linear"
                      dataKey="idealVolume"
                      name={t("wip.charts.ideal")}
                      stroke={colors.volume}
                      strokeWidth={2}
                      strokeDasharray="6 4"
                      strokeOpacity={0.6}
                      dot={false}
                      activeDot={false}
                      isAnimationActive={false}
                    />
                    {forecastLine("forecastVolume")}
                  </LineChart>
                </ResponsiveContainer>
              </Box>
              {goalColumns.length > 0 && (
                <WipGoalColumns
                  columns={goalColumns}
                  color={colors.volume}
                  height={chartHeight}
                />
              )}
            </Stack>
          </ChartCard>

          <ChartCard
            title={t(
              daily ? "wip.charts.costTitleDaily" : "wip.charts.costTitle"
            )}
            subtitle={t(
              daily ? "wip.charts.costSubtitleDaily" : "wip.charts.costSubtitle"
            )}
            height={chartHeight}
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={visiblePoints} margin={chartMargin}>
                {grid}
                {xAxis}
                <YAxis
                  tickFormatter={(v: number) => `${compact.format(v)} €`}
                  width={64}
                  {...axisProps}
                />
                <Tooltip
                  content={<ChartTooltip format={(n) => fmtMoney(n)} />}
                  cursor={{
                    stroke: theme.palette.text.secondary,
                    strokeWidth: 1,
                  }}
                />
                {legend}
                {todayBand}
                {asOfLine}
                <Line
                  type="monotone"
                  dataKey="platformCost"
                  name={t("wip.charts.platformCost")}
                  stroke={colors.money}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{
                    r: 4,
                    strokeWidth: 2,
                    stroke: theme.palette.background.paper,
                  }}
                  isAnimationActive={false}
                />
                <Line
                  type="linear"
                  dataKey="idealPlatformCost"
                  name={t("wip.charts.platformCostIdeal")}
                  stroke={colors.money}
                  strokeWidth={2}
                  strokeDasharray="6 4"
                  strokeOpacity={0.6}
                  dot={false}
                  activeDot={false}
                  isAnimationActive={false}
                />
                {forecastLine("forecastPlatformCost")}
                {initialInvestmentDot}
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            title={t("wip.charts.kpiTitle", { objective: row.objective })}
            subtitle={t(
              daily ? "wip.charts.kpiSubtitleDaily" : "wip.charts.kpiSubtitle"
            )}
            height={chartHeight}
          >
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={visiblePoints} margin={chartMargin}>
                {grid}
                {xAxis}
                <YAxis
                  tickFormatter={(v: number) => `${formatFlexible(v, 3)} €`}
                  width={64}
                  {...axisProps}
                />
                <Tooltip
                  content={<ChartTooltip format={(n) => fmtMoneyDetailed(n)} />}
                  cursor={{
                    stroke: theme.palette.text.secondary,
                    strokeWidth: 1,
                  }}
                />
                {legend}
                {todayBand}
                {asOfLine}
                <Line
                  type="monotone"
                  dataKey="realKpi"
                  name={t("wip.charts.kpiReal", { objective: row.objective })}
                  stroke={colors.money}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{
                    r: 4,
                    strokeWidth: 2,
                    stroke: theme.palette.background.paper,
                  }}
                  isAnimationActive={false}
                />
                {platformBriefKpi != null && (
                  <Line
                    type="linear"
                    dataKey="briefKpi"
                    name={t("wip.charts.kpiBrief", {
                      objective: row.objective,
                    })}
                    stroke={colors.money}
                    strokeWidth={2}
                    strokeDasharray="6 4"
                    strokeOpacity={0.6}
                    dot={false}
                    activeDot={false}
                    isAnimationActive={false}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </>
      )}
    </Stack>
  );
};

export default WipLineCharts;
